import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetPresentacionQuery } from '../../../../services/presentacionesApi';
import {
    useGetRecomendacionesVerosimilitudQuery,
    useGetTechnicalVisitQuery,
    useGetTechnicalVisitQuestionsQuery,
    useGetVerosimilitudByIdQuery
} from '../../../../services/visitasApi';
import {
    getTechnicalVisitResponses,
    normalizeTechnicalVisitFormDefinition,
    parseTechnicalVisitResponse
} from '../TechnicalVisit/utils/technicalVisitUtils';

type VerosimilitudRecomendacionesSectionProps = {
    idVisita: number;
    idVerosimilitud: number;
    idPresentacion: number;
};

const VerosimilitudRecomendacionesSection = ({
    idVisita,
    idVerosimilitud,
    idPresentacion
}: VerosimilitudRecomendacionesSectionProps) => {
    const { t } = useTranslation('visitas');

    const { data: verosimilitudData, isLoading: isLoadingVerosimilitud } =
        useGetVerosimilitudByIdQuery({ idVisita, idVerosimilitud });

    const { data: presentacionData, isLoading: isLoadingPresentacion } =
        useGetPresentacionQuery(idPresentacion);

    const { data: recomendacionesData, isLoading: isLoadingRecomendaciones } =
        useGetRecomendacionesVerosimilitudQuery();

    const isLoading = isLoadingVerosimilitud || isLoadingPresentacion || isLoadingRecomendaciones;

    const respuestas: any[] =
        (verosimilitudData as any)?.respuestas ??
        (verosimilitudData as any)?.data?.respuestas ??
        [];

    // idPregunta → idTipoRespuesta seleccionado
    const respuestaMap = new Map<number, number>(
        respuestas.map((r: any) => [
            Number(r.idPregunta ?? r.idSeccion),
            Number(r.idTipoRespuesta ?? r.idTipoRepuesta)
        ])
    );

    // idPregunta → fechaRegularizacion (null si no tiene)
    const fechaRegMap = new Map<number, string | null>(
        respuestas.map((r: any) => [
            Number(r.idPregunta ?? r.idSeccion),
            r.fechaRegularizacion ?? null
        ])
    );

    // idPregunta → texto de recomendación para incumple (idTipoRespuesta 2)
    const recomendacionMap = new Map<number, string>(
        (recomendacionesData?.data ?? []).flatMap((item) => {
            const rec = item.recomendaciones.find((r) => (r.idTipoRespuesta ?? 2) === 2);
            return rec ? [[item.id, rec.texto] as [number, string]] : [];
        })
    );

    // Agrupa por sección: para cada sección de la presentación acumula las
    // recomendaciones de sus preguntas marcadas como incumple.
    const secciones: any[] =
        (presentacionData as any)?.solapas?.[0]?.secciones ?? [];

    const seccionesConRecomendaciones = secciones
        .map((seccion: any) => {
            const recomendacionesDeSeccion: { texto: string; tieneFecha: boolean }[] = [];

            (seccion.preguntas ?? []).forEach((pregunta: any) => {
                const idPregunta = Number(pregunta.id);
                if (respuestaMap.get(idPregunta) !== 2) return;
                const rec = recomendacionMap.get(idPregunta);
                if (rec) {
                    recomendacionesDeSeccion.push({
                        texto: rec,
                        tieneFecha: !!fechaRegMap.get(idPregunta)
                    });
                }
            });

            return {
                id: seccion.id,
                titulo: seccion.titulo ?? seccion.nombre ?? '',
                recomendaciones: recomendacionesDeSeccion
            };
        })
        .filter((s: any) => s.recomendaciones.length > 0);

    if (isLoading) {
        return <CircularProgress size={20} />;
    }

    if (seccionesConRecomendaciones.length === 0) return null;

    return (
        <Box sx={{ mt: 0 }}>
            <Typography variant="h4" mb={2}>
                <strong>{t('verosimilitud.recommendations.sectionTitle')}</strong>
            </Typography>
            <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                {seccionesConRecomendaciones.map((seccion: any, index: number) => (
                    <Box key={seccion.id}>
                        {index > 0 && <Divider sx={{ my: 1.5 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {seccion.titulo}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3 }}>
                            {seccion.recomendaciones.map(
                                (rec: { texto: string; tieneFecha: boolean }, i: number) => (
                                    <Box component="li" key={i}>
                                        <Typography variant="body2" component="span">
                                            {rec.texto}
                                        </Typography>
                                        {!rec.tieneFecha && (
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                sx={{ color: 'error.main', ml: 0.5 }}
                                            >
                                                (Fecha de regularización pendiente)
                                            </Typography>
                                        )}
                                    </Box>
                                )
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

type TechnicalVisitRecomendacionesSectionProps = {
    idVisita: number;
    idVisitaTecnica: number;
};

const TechnicalVisitRecomendacionesSection = ({
    idVisita,
    idVisitaTecnica
}: TechnicalVisitRecomendacionesSectionProps) => {
    const { t } = useTranslation('visitas');

    const { data: questionsResponse, isLoading: isLoadingQuestions } =
        useGetTechnicalVisitQuestionsQuery({ idVisita });

    const { data: visitResponse, isLoading: isLoadingVisit } = useGetTechnicalVisitQuery({
        idVisita,
        idVisitaTecnica
    });

    const isLoading = isLoadingQuestions || isLoadingVisit;

    const formDefinition = normalizeTechnicalVisitFormDefinition(questionsResponse);
    const initialResponses = getTechnicalVisitResponses(visitResponse);

    // Agrupa por sección: para cada sección, acumula las recomendaciones de sus
    // preguntas que fueron marcadas como "No cumple" (requiereRecomendacion=true).
    const seccionesConRecomendaciones = formDefinition
        ? formDefinition.secciones
              .map((section) => {
                  const recomendacionesDeSeccion: { id: number; texto: string }[] = [];

                  section.preguntas.forEach((question) => {
                      const response = initialResponses.find((r: any) => {
                          const rId = Number(r?.idSeccion ?? r?.seccion?.id);
                          return rId === question.id;
                      });

                      const parsed = parseTechnicalVisitResponse(response, question);
                      const selectedAnswer = question.tiposRespuesta.find(
                          (a) => a.id === parsed.answerId
                      );

                      if (!selectedAnswer?.requiereRecomendacion) return;

                      const selectedRecIds = parsed.recomendacionesSeleccionadas ?? [];
                      const selectedRecs = question.recomendaciones.filter((r) =>
                          selectedRecIds.includes(r.id)
                      );

                      recomendacionesDeSeccion.push(...selectedRecs);
                  });

                  return {
                      id: section.id,
                      titulo: section.titulo,
                      recomendaciones: recomendacionesDeSeccion
                  };
              })
              .filter((s) => s.recomendaciones.length > 0)
        : [];

    if (isLoading) {
        return <CircularProgress size={20} />;
    }

    if (seccionesConRecomendaciones.length === 0) return null;

    return (
        <Box sx={{ pt: .5 }}>
            <Typography variant="h6" mt={0}>
                <strong>{t('technicalVisit.recommendations.sectionTitle')}</strong>
            </Typography>
            <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                {seccionesConRecomendaciones.map((seccion, index) => (
                    <Box key={seccion.id}>
                        {index > 0 && <Divider sx={{ my: 1.5 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {seccion.titulo}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3 }}>
                            {seccion.recomendaciones.map((rec) => (
                                <Box component="li" key={rec.id}>
                                    <Typography variant="body2">{rec.texto}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

type EjecucionRecomendacionesSectionProps = {
    idVisita: number;
    isVerosimilitud: boolean;
    hasVerosimilitudEntity: boolean;
    idVerosimilitud: number | null | undefined;
    idPresentacion: number | null;
    hasTechnicalVisit: boolean;
    isTechnicalVisitDone: boolean;
    idVisitaTecnica: number | null;
};

const EjecucionRecomendacionesSection = ({
    idVisita,
    isVerosimilitud,
    hasVerosimilitudEntity,
    idVerosimilitud,
    idPresentacion,
    hasTechnicalVisit,
    isTechnicalVisitDone,
    idVisitaTecnica
}: EjecucionRecomendacionesSectionProps) => {
    const showVerosimilitud = isVerosimilitud && hasVerosimilitudEntity && !!idVerosimilitud && !!idPresentacion;
    const showTechnicalVisit = hasTechnicalVisit && isTechnicalVisitDone && !!idVisitaTecnica;

    if (!showVerosimilitud && !showTechnicalVisit) return null;

    return (
        <>
            {showVerosimilitud && (
                <VerosimilitudRecomendacionesSection
                    idVisita={idVisita}
                    idVerosimilitud={idVerosimilitud!}
                    idPresentacion={idPresentacion!}
                />
            )}
            {showTechnicalVisit && (
                <TechnicalVisitRecomendacionesSection
                    idVisita={idVisita}
                    idVisitaTecnica={idVisitaTecnica!}
                />
            )}
        </>
    );
};

export default EjecucionRecomendacionesSection;
